import { EntityImage, Images, useDebouncedState } from '@urbanx/agx-ui-components';
import { GetAllAgentsDetailsProjection, useStaffGetAllAgents } from '@urbanx/agx.agencies.client';
import {
  AgxColumn,
  AgxRow,
  AgxTable,
  AgxTextInput,
  AgxTypography,
  IColumnConfig,
} from '@urbanx/agx.ui-components2';
import CleanPanel from 'components/cleanPanel/CleanPanel';
import LoadingState from 'components/loadingState/LoadingState';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

const preferencesCols: (string | IColumnConfig)[] = ['Partner'];

const formatName = (agent: GetAllAgentsDetailsProjection) => {
  return [agent.name?.firstName, agent.name?.lastName]
    .filter((piece) => piece && piece.length > 0)
    .join(' ');
};

const IndividualsPage = () => {
  const { data: agents, isPending: isLoadingAgents } = useStaffGetAllAgents();

  const [searchText, setSearchText] = useDebouncedState('', 300);

  const filteredAgents = useMemo(() => {
    const lowerCaseSearch = searchText.toLowerCase();

    if (!lowerCaseSearch || lowerCaseSearch === '') return agents ?? [];

    return (agents ?? []).filter((agent) =>
      agent?.name?.fullName?.toLowerCase().includes(lowerCaseSearch)
    );
  }, [searchText, agents]);

  const rows = useMemo(() => {
    return filteredAgents.map((agent) => {
      const name = formatName(agent);

      return {
        ['Partner']: (
          <AgxRow
            gap="spacing-lg"
            key={agent.id}
            justifyContent="left"
            fill
            alignItems="alignCenter"
          >
            <EntityImage addRingToImage={false} name={name} photoUri={agent.photoUri} />
            <AgxTypography id={`partner-${agent.id}`} type="body" size="small">
              {name}
            </AgxTypography>
          </AgxRow>
        ),
      };
    });
  }, [filteredAgents]);

  if (isLoadingAgents) {
    return <LoadingState />;
  }

  return (
    <CleanPanel>
      <AgxColumn gap={'spacing-2xl'} fill justifyContent="left" padding="spacing-2xl">
        <AgxTextInput
          id="individualSearch"
          leftIcon={<Images.SearchOutline />}
          noHeader
          type="text"
          hideOptionalText
          placeholder="Search"
          stretch
          defaultValue={searchText}
          onChange={(value) => setSearchText(value)}
        />
        <AgxTable
          columns={preferencesCols}
          rows={rows}
          variant="default"
          renderRowLink={(index, linkProps) => {
            const agent = filteredAgents[index];
            return agent.id ? (
              <Link
                {...linkProps}
                to={`./${agent.id}`}
                aria-label={formatName(agent).trim() || `Agent ${agent.id}`}
              />
            ) : undefined;
          }}
        />
      </AgxColumn>
    </CleanPanel>
  );
};

export default IndividualsPage;
